// Internal "Office Copy" invoice PDF — admin/owner-only print that shows
// every line's COST vs CHARGE vs MARGIN. Mirrors the Quote office-copy
// layout from QuoteView (project_internal_itemized_quote) so the
// bookkeeper / owner has a consistent review surface across the quote
// pipeline AND the invoice pipeline.
//
// Watermarked OFFICE COPY — NOT FOR CUSTOMER. Sent through the same
// PdfActionModal print/save chooser as every other PDF in the app.
//
// payload:
//   orgName, orgAddress, logoUrl
//   invoiceNumber, jobNumber, jobTitle
//   customerName, locationName
//   costing           — { labor, materials, expenses, fuel, charges, taxes }
//                       Already filtered to this bill's line subset on the
//                       multi-bill (Progress Bill) path; equal to job.costing
//                       on legacy single-finalize jobs.
//   taxExempt, taxExemptCert
//   bill              — optional { grandTotal, subtotal } for cross-check
//                       against computed totals
//   finalizedAt       — ISO timestamp (rendered as Date)

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { loadImageAsDataUrl } from "./pdfExport";

const money = (n) => {
  const v = typeof n === "number" ? n : parseFloat(n) || 0;
  return v.toLocaleString("en-US", { style: "currency", currency: "USD" });
};
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const addrLineList = (addr = {}) => {
  const lines = [];
  if (addr.street) lines.push(addr.street);
  const cityLine = [addr.city, addr.state, addr.zip]
    .filter(Boolean)
    .join(", ");
  if (cityLine) lines.push(cityLine);
  if (addr.phone) lines.push(addr.phone);
  return lines;
};

const fuelLineCost = (f) => {
  if (f.mode === "flat") return num(f.amount);
  return num(f.costPerMile) * num(f.milesOneWay) * 2 * num(f.trips);
};
const fuelLineWithMarkup = (f) => fuelLineCost(f) * (1 + num(f.markup) / 100);

export async function buildInvoiceOfficeCopyDoc(payload = {}) {
  const {
    orgName = "",
    orgAddress = {},
    logoUrl = "",
    invoiceNumber = "",
    jobNumber = "",
    jobTitle = "",
    customerName = "-",
    locationName = "",
    costing = {},
    taxExempt = false,
    taxExemptCert = "",
    finalizedAt = "",
  } = payload;

  const c = costing || {};

  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const PAGE_W = doc.internal.pageSize.getWidth();
  const MARGIN = 40;
  const CONTENT_W = PAGE_W - MARGIN * 2;

  // --- Watermark banner ---
  doc.setFillColor(255, 235, 235);
  doc.rect(0, 0, PAGE_W, 28, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(150, 30, 30);
  doc.text(
    "OFFICE COPY - NOT FOR CUSTOMER - INTERNAL P&L REVIEW",
    MARGIN,
    18
  );

  // --- Header (logo + brand) ---
  const logoData = await loadImageAsDataUrl(logoUrl);
  let titleX = MARGIN;
  const headerTop = MARGIN + 14;
  if (logoData) {
    try {
      doc.addImage(logoData, "AUTO", MARGIN, headerTop, 80, 60);
      titleX = MARGIN + 90;
    } catch (err) {
      console.warn("invoice office copy: logo addImage failed:", err);
    }
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(34, 34, 34);
  const titleStr = invoiceNumber
    ? `Invoice ${invoiceNumber} - Office Copy`
    : jobNumber
    ? `Job #${jobNumber} - Office Copy`
    : "Invoice - Office Copy";
  doc.text(titleStr, titleX, headerTop + 18);

  let headerY = headerTop + 18;
  if (orgName) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(102, 102, 102);
    headerY += 16;
    doc.text(orgName, titleX, headerY);
  }
  doc.setFontSize(10);
  doc.setTextColor(85, 85, 85);
  for (const line of addrLineList(orgAddress)) {
    headerY += 12;
    doc.text(line, titleX, headerY);
  }
  headerY = Math.max(headerY, headerTop + 60);

  // --- Doc meta ---
  let y = headerY + 28;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(26, 95, 180);
  const metaLabel = jobNumber
    ? `Job #${jobNumber}${jobTitle ? ` - ${jobTitle}` : ""}`
    : invoiceNumber || "Invoice";
  doc.text(metaLabel, MARGIN, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(34, 34, 34);
  const dateStr = `Date: ${
    finalizedAt
      ? new Date(finalizedAt).toLocaleDateString()
      : new Date().toLocaleDateString()
  }`;
  doc.text(dateStr, PAGE_W - MARGIN - doc.getTextWidth(dateStr), y);

  // --- Parties ---
  y += 22;
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("Customer:", MARGIN, y);
  doc.setFont("helvetica", "normal");
  doc.text(customerName || "-", MARGIN + 70, y);
  if (locationName) {
    y += 14;
    doc.setFont("helvetica", "bold");
    doc.text("Location:", MARGIN, y);
    doc.setFont("helvetica", "normal");
    doc.text(locationName, MARGIN + 70, y);
  }
  y += 20;

  // --- Internal pricing table: Description / Detail / Cost / Charge / Margin ---
  const body = [];
  let totalCost = 0;
  let totalCharge = 0;

  (c.labor || []).forEach((l) => {
    const hrs = num(l.hours);
    const cost = hrs * num(l.cost);
    const charge = hrs * num(l.price || l.rate);
    totalCost += cost;
    totalCharge += charge;
    body.push([
      `Labor${l.description ? " - " + l.description : ""}`,
      `${hrs} hr @ ${money(num(l.cost))} cost / ${money(
        num(l.price || l.rate)
      )} charge`,
      money(cost),
      money(charge),
      money(charge - cost),
    ]);
  });

  (c.materials || []).forEach((m) => {
    const qty = num(m.qty);
    const unitCost = num(m.unitCost || m.cost);
    const markup = num(m.markup);
    const cost = qty * unitCost;
    const charge = cost * (1 + markup / 100);
    totalCost += cost;
    totalCharge += charge;
    body.push([
      `${m.name || "Material"}${m.partNumber ? ` (#${m.partNumber})` : ""}`,
      `${qty} @ ${money(unitCost)} + ${markup}% markup`,
      money(cost),
      money(charge),
      money(charge - cost),
    ]);
  });

  (c.expenses || []).forEach((e) => {
    const amount = num(e.amount);
    const markup = num(e.markup);
    const charge = amount * (1 + markup / 100);
    totalCost += amount;
    totalCharge += charge;
    body.push([
      e.description || "Expense",
      `${money(amount)} + ${markup}% markup`,
      money(amount),
      money(charge),
      money(charge - amount),
    ]);
  });

  (c.charges || []).forEach((x) => {
    const qty = num(x.qty);
    const unitCost = num(x.cost);
    const markup = num(x.markup);
    const cost = qty * unitCost;
    const charge = cost * (1 + markup / 100);
    totalCost += cost;
    totalCharge += charge;
    body.push([
      x.description || "Charge",
      `${qty} @ ${money(unitCost)} + ${markup}% markup`,
      money(cost),
      money(charge),
      money(charge - cost),
    ]);
  });

  (c.fuel || []).forEach((f) => {
    const markup = num(f.markup);
    const cost = fuelLineCost(f);
    const charge = fuelLineWithMarkup(f);
    totalCost += cost;
    totalCharge += charge;
    const label =
      f.mode === "flat"
        ? f.description || "Trip charge"
        : "Fuel / trip (mileage)";
    const detail =
      f.mode === "flat"
        ? `flat ${money(num(f.amount))} + ${markup}% markup`
        : `${num(f.trips)} trip(s) x ${num(f.milesOneWay)} mi 1-way @ ${money(
            num(f.costPerMile)
          )}/mi + ${markup}% markup`;
    body.push([label, detail, money(cost), money(charge), money(charge - cost)]);
  });

  // Tax + fee lines — shown for reconciliation against the customer-facing
  // invoice total. Cost column blank since taxes pass through to gov.
  let taxTotal = 0;
  (c.taxes || []).forEach((t) => {
    const amt = taxExempt
      ? 0
      : t.amount !== undefined
      ? num(t.amount)
      : t.mode === "flat"
      ? num(t.amount)
      : totalCharge * (num(t.rate) / 100);
    taxTotal += amt;
    body.push([
      taxExempt
        ? `Sales tax - EXEMPT${taxExemptCert ? ` (Cert ${taxExemptCert})` : ""}`
        : `${t.name || "Tax/fee"}${
            t.mode === "percent" && num(t.rate) ? ` (${num(t.rate)}%)` : ""
          }`,
      t.mode === "flat" ? "flat fee" : "% of taxable subtotal",
      "-",
      money(amt),
      "-",
    ]);
  });

  autoTable(doc, {
    startY: y,
    head: [["Description", "Detail", "Cost", "Charge", "Margin"]],
    body,
    foot: [
      [
        { content: "Totals (pre-tax)", colSpan: 2 },
        { content: money(totalCost), styles: { halign: "right" } },
        { content: money(totalCharge), styles: { halign: "right" } },
        {
          content: money(totalCharge - totalCost),
          styles: { halign: "right" },
        },
      ],
    ],
    theme: "plain",
    margin: { left: MARGIN, right: MARGIN, top: 60, bottom: 60 },
    styles: { fontSize: 10, cellPadding: 5, overflow: "linebreak" },
    headStyles: {
      fontStyle: "bold",
      fillColor: false,
      textColor: 34,
      lineWidth: { bottom: 0.75 },
      lineColor: 200,
    },
    bodyStyles: { lineWidth: { bottom: 0.4 }, lineColor: 215 },
    footStyles: {
      fontStyle: "bold",
      fillColor: false,
      textColor: 34,
      lineWidth: { top: 1.25 },
      lineColor: 80,
    },
    columnStyles: {
      0: { cellWidth: "auto" },
      1: { cellWidth: 130 },
      2: { cellWidth: 70, halign: "right" },
      3: { cellWidth: 70, halign: "right" },
      4: { cellWidth: 70, halign: "right" },
    },
  });

  // --- Margin summary block ---
  const grossProfit = totalCharge - totalCost;
  const grossMarginPct =
    totalCharge > 0 ? (grossProfit / totalCharge) * 100 : 0;
  const grossMarkupPct = totalCost > 0 ? (grossProfit / totalCost) * 100 : 0;
  const customerTotal = totalCharge + taxTotal;

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 12,
    theme: "plain",
    margin: { left: MARGIN, right: MARGIN, top: 60, bottom: 60 },
    body: [
      [
        { content: "Total cost (internal)" },
        { content: money(totalCost), styles: { halign: "right" } },
      ],
      [
        { content: "Total charge (pre-tax)" },
        { content: money(totalCharge), styles: { halign: "right" } },
      ],
      [
        {
          content: "Gross profit (charge - cost)",
          styles: { fontStyle: "bold" },
        },
        {
          content: money(grossProfit),
          styles: { halign: "right", fontStyle: "bold" },
        },
      ],
      [
        { content: "Margin % (profit / charge)" },
        {
          content: `${grossMarginPct.toFixed(1)}%`,
          styles: { halign: "right" },
        },
      ],
      [
        { content: "Markup % (profit / cost)" },
        {
          content: `${grossMarkupPct.toFixed(1)}%`,
          styles: { halign: "right" },
        },
      ],
      [
        { content: "Taxes & fees" },
        { content: money(taxTotal), styles: { halign: "right" } },
      ],
      [
        {
          content: "Customer total (the number on the customer invoice)",
          styles: { fontStyle: "bold", fontSize: 12 },
        },
        {
          content: money(customerTotal),
          styles: { halign: "right", fontStyle: "bold", fontSize: 12 },
        },
      ],
    ],
    styles: { fontSize: 10, cellPadding: 5 },
    bodyStyles: { lineWidth: { bottom: 0.4 }, lineColor: 215 },
    columnStyles: { 1: { halign: "right" } },
  });

  // --- Footer watermark ---
  const footY = doc.lastAutoTable.finalY + 24;
  doc.setFont("helvetica", "italic");
  doc.setFontSize(10);
  doc.setTextColor(150, 30, 30);
  doc.text(
    "OFFICE COPY - internal P&L review. Do not send to customer.",
    MARGIN,
    footY
  );

  return doc;
}

// Build the costing subset for a specific bill on a multi-bill job.
// Returns { labor, materials, expenses, fuel, charges, taxes } shaped for
// buildInvoiceOfficeCopyDoc. For legacy single-finalize jobs (no bill arg),
// returns the full job.costing.
export function costingForBill(job, bill) {
  const base = job?.costing || {};
  if (!bill) {
    return {
      labor: base.labor || [],
      materials: base.materials || [],
      expenses: base.expenses || [],
      fuel: base.fuel || [],
      charges: base.charges || [],
      taxes: base.taxes || [],
    };
  }
  const ids = new Set(bill.lineItemIds || []);
  const filter = (arr) => (arr || []).filter((l) => ids.has(l.id));
  return {
    labor: filter(base.labor),
    materials: filter(base.materials),
    expenses: filter(base.expenses),
    fuel: filter(base.fuel),
    charges: filter(base.charges),
    taxes: bill.taxes || [],
  };
}
